import { debugLog } from './debugConfig';

/**
 * Login Attempt Tracker - Brute Force Protection
 * Tracks failed login attempts and implements lockout mechanism
 */

interface LoginAttempt {
  count: number;
  lockedUntil: number | null;
  lastAttempt: number;
}

class LoginAttemptTracker {
  private attempts: Map<string, LoginAttempt> = new Map();
  private readonly MAX_ATTEMPTS = 5; // Industry standard: 3-5 attempts
  private readonly LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes (industry standard)
  private readonly CLEANUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
  private readonly ATTEMPT_RESET_TIME_MS = 30 * 60 * 1000; // 30 minutes
  private readonly STORAGE_KEY = 'login_attempts';

  constructor() {
    // Load existing attempts from localStorage
    this.loadFromStorage();
    
    // Periodically clean up old entries
    setInterval(() => this.cleanup(), this.CLEANUP_INTERVAL_MS);
    
    // Save to storage periodically
    setInterval(() => this.saveToStorage(), 10000); // Save every 10 seconds
  }

  /**
   * Load attempts from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.attempts = new Map(Object.entries(data));
        void debugLog('📂 Loaded login attempts from storage');
      }
    } catch (error) {
      console.error('Failed to load login attempts from storage:', error);
    }
  }

  /**
   * Save attempts to localStorage
   */
  private saveToStorage(): void {
    try {
      const data = Object.fromEntries(this.attempts);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save login attempts to storage:', error);
    }
  }

  /**
   * Check if an email is currently locked out
   */
  isLocked(email: string): boolean {
    const normalizedEmail = email.toLowerCase().trim();
    const attempt = this.attempts.get(normalizedEmail);

    if (!attempt || !attempt.lockedUntil) {
      return false;
    }

    const now = Date.now();
    
    // Check if lockout period has expired
    if (now >= attempt.lockedUntil) {
      // Reset the attempt after lockout expires
      this.attempts.delete(normalizedEmail);
      this.saveToStorage();
      return false;
    }

    return true;
  }

  /**
   * Get remaining lockout time in seconds
   */
  getRemainingLockoutTime(email: string): number {
    const normalizedEmail = email.toLowerCase().trim();
    const attempt = this.attempts.get(normalizedEmail);

    if (!attempt || !attempt.lockedUntil) {
      return 0;
    }

    const now = Date.now();
    const remaining = Math.max(0, attempt.lockedUntil - now);
    
    return Math.ceil(remaining / 1000); // Convert to seconds
  }

  /**
   * Record a failed login attempt
   */
  recordFailedAttempt(email: string): void {
    const normalizedEmail = email.toLowerCase().trim();
    const now = Date.now();
    const attempt = this.attempts.get(normalizedEmail);

    if (!attempt) {
      // First failed attempt
      this.attempts.set(normalizedEmail, {
        count: 1,
        lockedUntil: null,
        lastAttempt: now
      });
      this.saveToStorage();
      return;
    }

    // Check if we should reset the counter (if last attempt was too long ago)
    if (now - attempt.lastAttempt > this.ATTEMPT_RESET_TIME_MS) {
      this.attempts.set(normalizedEmail, {
        count: 1,
        lockedUntil: null,
        lastAttempt: now
      });
      this.saveToStorage();
      return;
    }

    // Increment attempt count
    const newCount = attempt.count + 1;

    if (newCount >= this.MAX_ATTEMPTS) {
      // Lock the account
      this.attempts.set(normalizedEmail, {
        count: newCount,
        lockedUntil: now + this.LOCKOUT_DURATION_MS,
        lastAttempt: now
      });
      this.saveToStorage();
      void debugLog(`🔒 Account locked for ${normalizedEmail} due to too many failed attempts`);
    } else {
      // Update attempt count
      this.attempts.set(normalizedEmail, {
        count: newCount,
        lockedUntil: null,
        lastAttempt: now
      });
      this.saveToStorage();
      void debugLog(`⚠️ Failed login attempt ${newCount}/${this.MAX_ATTEMPTS} for ${normalizedEmail}`);
    }
  }

  /**
   * Record a successful login (reset attempts)
   */
  recordSuccessfulLogin(email: string): void {
    const normalizedEmail = email.toLowerCase().trim();
    this.attempts.delete(normalizedEmail);
    this.saveToStorage();
  }

  /**
   * Get current attempt count for an email
   */
  getAttemptCount(email: string): number {
    const normalizedEmail = email.toLowerCase().trim();
    const attempt = this.attempts.get(normalizedEmail);
    return attempt ? attempt.count : 0;
  }

  /**
   * Get remaining attempts before lockout
   */
  getRemainingAttempts(email: string): number {
    const normalizedEmail = email.toLowerCase().trim();
    const attempt = this.attempts.get(normalizedEmail);
    const count = attempt ? attempt.count : 0;
    return Math.max(0, this.MAX_ATTEMPTS - count);
  }

  /**
   * Manually unlock an account (for admin use)
   */
  unlock(email: string): void {
    const normalizedEmail = email.toLowerCase().trim();
    this.attempts.delete(normalizedEmail);
    this.saveToStorage();
    void debugLog(`🔓 Account manually unlocked for ${normalizedEmail}`);
  }

  /**
   * Clean up old entries
   */
  private cleanup(): void {
    const now = Date.now();
    const toDelete: string[] = [];

    this.attempts.forEach((attempt, email) => {
      // Remove entries that are old and not locked
      if (!attempt.lockedUntil && now - attempt.lastAttempt > this.ATTEMPT_RESET_TIME_MS) {
        toDelete.push(email);
      }
      // Remove entries where lockout has expired
      else if (attempt.lockedUntil && now >= attempt.lockedUntil) {
        toDelete.push(email);
      }
    });

    toDelete.forEach(email => this.attempts.delete(email));
    
    if (toDelete.length > 0) {
      this.saveToStorage();
      void debugLog(`🧹 Cleaned up ${toDelete.length} old login attempt entries`);
    }
  }

  /**
   * Get all locked accounts (for admin monitoring)
   */
  getLockedAccounts(): Array<{ email: string; remainingSeconds: number }> {
    const now = Date.now();
    const locked: Array<{ email: string; remainingSeconds: number }> = [];

    this.attempts.forEach((attempt, email) => {
      if (attempt.lockedUntil && now < attempt.lockedUntil) {
        locked.push({
          email,
          remainingSeconds: Math.ceil((attempt.lockedUntil - now) / 1000)
        });
      }
    });

    return locked;
  }
}

// Export singleton instance
export const loginAttemptTracker = new LoginAttemptTracker();
